import { ChildApplyDomainService } from './domain';
import { ChildApplyFactory } from './entity/childApplyFactory';
import { ChildRemoveFactory } from './entity/childRemoveFactory';
import { FacilityFactory } from './entity/facilityFactory';

import type { ChildApplyIRepository } from './entity/childApplyIRepository';
import type { ChildRemoveIRepository } from './entity/childRemoveIRepository';
import type { FacilityIRepository } from './entity/facilityIRepository';
import type { HomepageSettingsIRepository } from './entity/homepageSettingsIRepository';
import type { EntityDataPubType } from '@/domain/entityBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { ChildApplyJsonPub, ChildApplyJsonPvt } from '@/types/domain/childApply/entities/childApplyEntity';
import type { ChildRemoveJsonPub, ChildRemoveJsonPvt } from '@/types/domain/childApply/entities/childRemoveEntity';
import type { FacilityJson, FacilityJsonPub, FacilityJsonPvt } from '@/types/domain/childApply/entities/facilityEntity';

/**
 * ChildApplyApplicationService
 */
export class ChildApplyApplicationService {
  private service!: ChildApplyDomainService;

  private childApplyFactory = new ChildApplyFactory();

  private childRemoveFactory = new ChildRemoveFactory();

  private facilityFactory = new FacilityFactory();

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param childApplyRepository childApplyRepository
   * @param childRemoveRepository childRemoveRepository
   * @param facilityRepository facilityRepository
   * @param homepageSettingsRepository homepageSettingsRepository
   * @param logger logger
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    childApplyRepository: ChildApplyIRepository,
    childRemoveRepository: ChildRemoveIRepository,
    facilityRepository: FacilityIRepository,
    homepageSettingsRepository: HomepageSettingsIRepository,
    logger: Logger,
  ) {
    this.service = new ChildApplyDomainService(logger);
    this.service.inject(
      wagakoNurseryApi,
      childApplyRepository,
      childRemoveRepository,
      facilityRepository,
      homepageSettingsRepository,
    );
  }

  /**
   * getChildApplyList
   * @param token トークン
   * @returns リスト
   */
  async getChildApplyList(token: string | null) {
    const result = await this.service.getChildApplyList(token);
    return result;
  }

  /**
   * getChildRemoveList
   * @param token トークン
   * @returns リスト
   */
  async getChildRemoveList(token: string | null) {
    const result = await this.service.getChildRemoveList(token);
    return result;
  }

  /**
   * getFacilityList
   * @returns リスト
   */
  async getFacilityList() {
    const result = await this.service.getFacilityList();
    return result.map((v) => v.toJson());
  }

  /**
   * getFacilityHomepageSettings
   * @param nurseryId 施設ID
   * @param token トークン
   * @returns 結果
   */
  async getFacilityHomepageSettings(
    nurseryId: string,
    token: string | null = null,
  ) {
    const result = await this.service.getFacilityHomepageSettings(nurseryId, token);
    return result;
  }

  /**
   * getFacilityHomepageSettingsList
   * @param facilityList facilityList
   * @returns 結果
   */
  async getFacilityHomepageSettingsList(
    facilityList: FacilityJson[],
  ) {
    const result = await this.service.getFacilityHomepageSettingsList(facilityList);
    return result;
  }

  /**
   * sendChildApply
   * @param input input
   */
  async sendChildApply(
    input: EntityDataPubType<ChildApplyJsonPvt, ChildApplyJsonPub>,
  ) {
    const entity = this.childApplyFactory.createFromJson(input);
    await this.service.sendChildApply(entity);
  }

  /**
   * 子ども申請リストを更新する(Create)
   * @param childApplyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createChildApplyStore(
    childApplyList: EntityDataPubType<ChildApplyJsonPvt, ChildApplyJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: ChildApplyJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      childApplyList,
      payload,
      this.childApplyFactory,
      filter,
    );
  }

  /**
   * 子ども申請リストを更新する(Update)
   * @param childApplyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateChildApplyStore(
    childApplyList: EntityDataPubType<ChildApplyJsonPvt, ChildApplyJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      childApplyList,
      payload,
      this.childApplyFactory,
    );
  }

  /**
   * 子ども申請リストを更新する(Delete)
   * @param childApplyList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteChildApplyStore(
    childApplyList: EntityDataPubType<ChildApplyJsonPvt, ChildApplyJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      childApplyList,
      payload,
      this.childApplyFactory,
    );
  }

  /**
   * 子ども退園リストを更新する(Create)
   * @param childRemoveList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createChildRemoveStore(
    childRemoveList: EntityDataPubType<ChildRemoveJsonPvt, ChildRemoveJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: ChildRemoveJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      childRemoveList,
      payload,
      this.childRemoveFactory,
      filter,
    );
  }

  /**
   * 子ども退園リストを更新する(Update)
   * @param childRemoveList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateChildRemoveStore(
    childRemoveList: EntityDataPubType<ChildRemoveJsonPvt, ChildRemoveJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      childRemoveList,
      payload,
      this.childRemoveFactory,
    );
  }

  /**
   * 子ども退園リストを更新する(Delete)
   * @param childRemoveList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteChildRemoveStore(
    childRemoveList: EntityDataPubType<ChildRemoveJsonPvt, ChildRemoveJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      childRemoveList,
      payload,
      this.childRemoveFactory,
    );
  }

  /**
   * 施設リストを更新する(Create)
   * @param facilityList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createFacilityStore(
    facilityList: EntityDataPubType<FacilityJsonPvt, FacilityJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: FacilityJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      facilityList,
      payload,
      this.facilityFactory,
      filter,
    );
  }

  /**
   * 施設リストを更新する(Update)
   * @param facilityList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateFacilityStore(
    facilityList: EntityDataPubType<FacilityJsonPvt, FacilityJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      facilityList,
      payload,
      this.facilityFactory,
    );
  }

  /**
   * 施設リストを更新する(Delete)
   * @param facilityList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteFacilityStore(
    facilityList: EntityDataPubType<FacilityJsonPvt, FacilityJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      facilityList,
      payload,
      this.facilityFactory,
    );
  }
}
